import {
  ColumnData,
  RoutineLabelPickerProvider,
  RoutineLabelPickerRequest,
  SlideAction,
  SlideLayout,
  ViewMode
} from '@gripp/shared-ui';
import React, { useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AssetRouteProvider } from '../assetRouteContext';
import { RoutineSubmitSource } from '../assetUtils';
import { AssetRoutineSubmitColumn } from './asset-routine-submit-column';
import { AssetRoutineSubmitHeader } from './asset-routine-submit-header';
import { RoutineAllColumn } from './asset-routines-all-column';
import { AssetRoutinesHeader } from './asset-routines-header';
import { AssetCard } from './assetCard';

export const AssetRoutineSubmit = () => {
  const { assetId, routineId, issueId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const backGuardRef = useRef<((onProceed: () => void, onCancel?: () => void) => void) | null>(null);
  const [labelPickerRequest, setLabelPickerRequest] =
    useState<RoutineLabelPickerRequest | null>(null);

  if (!assetId || !routineId) {
    return null;
  }

  const from = searchParams.get('from');
  const originalSource = from ?? RoutineSubmitSource.AssetCard;
  const cameFromRoutinesList = originalSource === RoutineSubmitSource.RoutinesList;

  const backButtonGuard = (): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      if (!backGuardRef.current) {
        resolve(true);
        return;
      }
      backGuardRef.current(
        () => resolve(true),
        () => resolve(false)
      );
    });
  };

  const leftColumn = {
    title: 'assets.routines',
    mainComponent: (
      cameFromRoutinesList ? (
        <RoutineAllColumn assetId={assetId} />
      ) : (
        <AssetCard assetId={assetId} />
      )
    ),
    headerComponent: (
      <AssetRoutinesHeader assetId={assetId} />
    ),
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    backButtonGuard,
    onBackButtonPress: () => navigate(issueId ? '../../../..' : '../..', { relative: 'path' }),
  };

  const rightColumn: ColumnData = {
    title: 'routines.editRoutines.editRoutinesButton',
    mainComponent: (
      <RoutineLabelPickerProvider onRequestChange={setLabelPickerRequest}>
        <AssetRoutineSubmitColumn
          assetId={assetId}
          backGuardRef={backGuardRef}
        />
      </RoutineLabelPickerProvider>
    ),
    showBackButton: true,
    disableScroll: true,
    headerComponent: (
      <AssetRoutineSubmitHeader
        assetId={assetId}
        routineId={routineId}
      />
    ),
    backButtonTransition: SlideAction.PREV,
    backButtonGuard,
    onBackButtonPress: () => {
      const url = cameFromRoutinesList ? '../all' : '../..';
      navigate(url, { relative: 'path' });
    }
  };

  return (
    <AssetRouteProvider value={{
      basePath: issueId ? `/activities/${issueId}/asset/${assetId}` : `/assets/${assetId}`,
      listPath: issueId ? '/activities' : '/assets',
      isWorkspaceMode: false,
      issueId,
    }}>
      <SlideLayout
        leftColumn={leftColumn}
        rightColumn={rightColumn}
        viewMode={ViewMode.DUAL}
      />
    </AssetRouteProvider>
  );
};
